import { CHART_BREAKPOINTS, CHART_TEXT_LIMITS, CHART_LAYOUT_DEFAULTS, CHART_HIGHLIGHT, COLOR_FORMAT } from './chartConstants';

// Function to generate colors dynamically based on data length
// Colors will cycle through the palette if there are more data points than colors
export const generateColors = (dataLength: number, colorArray: string[]): string[] => {
    const colors = [];
    for (let i = 0; i < dataLength; i++) {
        colors.push(colorArray[i % colorArray.length]);
    }
    return colors;
};

// Function to truncate text based on screen size
export const truncateText = (text: string, screenWidth: number): string => {
    const maxLength = screenWidth < CHART_BREAKPOINTS.MOBILE ? 
        CHART_TEXT_LIMITS.MOBILE : 
        screenWidth < CHART_BREAKPOINTS.SMALL ?
        CHART_TEXT_LIMITS.SMALL :
        screenWidth < CHART_BREAKPOINTS.MEDIUM ? 
        CHART_TEXT_LIMITS.MEDIUM : 
        screenWidth < CHART_BREAKPOINTS.LARGE ? 
        CHART_TEXT_LIMITS.MEDIUM : 
        CHART_TEXT_LIMITS.LARGE;
    
    return text.length > maxLength 
        ? text.substring(0, maxLength) + '...'
        : text;
};

// Function to get responsive padding based on screen size
export const getResponsivePadding = (screenWidth: number) => {
    return {
        left: screenWidth < CHART_BREAKPOINTS.MOBILE ? 
            CHART_LAYOUT_DEFAULTS.PADDING.LEFT.MOBILE : 
            screenWidth < CHART_BREAKPOINTS.LARGE ? 
            CHART_LAYOUT_DEFAULTS.PADDING.LEFT.MEDIUM : 
            CHART_LAYOUT_DEFAULTS.PADDING.LEFT.LARGE,
        right: screenWidth < CHART_BREAKPOINTS.MOBILE ? 
            CHART_LAYOUT_DEFAULTS.PADDING.RIGHT.MOBILE : 
            CHART_LAYOUT_DEFAULTS.PADDING.RIGHT.DESKTOP,
        top: CHART_LAYOUT_DEFAULTS.PADDING.TOP,
        bottom: CHART_LAYOUT_DEFAULTS.PADDING.BOTTOM
    };
};

// Function to create hover handlers for legend items
export const createLegendHoverHandlers = (chartLabels: string[]) => {
    return {
        onHover: function(event: any, legendItem: any, legend: any) {
            if (legendItem && legendItem.index !== undefined) {
                const fullLabel = chartLabels[legendItem.index];
                if (fullLabel) {
                    event.native.target.style.cursor = 'pointer';
                    event.native.target.title = fullLabel;
                }
            }
        },
        onLeave: function(event: any) {
            event.native.target.style.cursor = 'default';
            event.native.target.title = '';
        }
    };
};

// Function to create standard tooltip callbacks
export const createTooltipCallbacks = (chartLabels: string[]) => {
    return {
        title: function(context: any) {
            return chartLabels[context[0].dataIndex] || '';
        },
        label: function(context: any) {
            const label = chartLabels[context.dataIndex] || '';
            const value = context.raw;
            return `${label}: ${value}`;
        }
    };
};

// Function to generate legend labels with truncation
export const generateLegendLabels = (chart: any, screenWidth: number, isDark?: boolean) => {
    const data = chart.data;
    const dark = isDark ?? document.documentElement.getAttribute('data-theme') === 'dark';
    const legendTextColor = dark ? 'rgba(255, 255, 255, 0.85)' : '#000000';

    if (data.labels && data.datasets.length) {
        const dataset = data.datasets[0];
        return data.labels.map((label: string, i: number) => {
            const truncatedLabel = truncateText(label, screenWidth);
            
            return {
                text: truncatedLabel,
                fillStyle: dataset.backgroundColor[i],
                strokeStyle: dataset.borderColor[i],
                lineWidth: dataset.borderWidth,
                pointStyle: 'circle',
                hidden: false,
                index: i,
                datasetIndex: 0,
                fontColor: legendTextColor,
            };
        });
    }
    return [];
};

// Color manipulation utilities for chart highlighting
export const adjustColorOpacity = (color: string, opacity: number): string => {
    // Handle RGB format: rgb(r, g, b) -> rgba(r, g, b, opacity)
    if (color.startsWith(COLOR_FORMAT.RGB_PREFIX)) {
        const rgbValues = color.slice(COLOR_FORMAT.RGB_PREFIX_LENGTH, COLOR_FORMAT.CLOSING_PAREN_OFFSET);
        return `rgba(${rgbValues}, ${opacity})`;
    }
    
    // Handle RGBA format: rgba(r, g, b, a) -> rgba(r, g, b, opacity)
    if (color.startsWith(COLOR_FORMAT.RGBA_PREFIX)) {
        const values = color.slice(COLOR_FORMAT.RGBA_PREFIX_LENGTH, COLOR_FORMAT.CLOSING_PAREN_OFFSET);
        const parts = values.split(',').map(part => part.trim());
        
        // Keep first 3 parts (r, g, b), replace the 4th part (alpha) with new opacity
        const rgbParts = parts.slice(0, COLOR_FORMAT.RGB_COMPONENT_COUNT).join(', ');
        return `rgba(${rgbParts}, ${opacity})`;
    }
    
    // Handle hex colors and other formats - return as is (could be extended later)
    return color;
};

// Create highlight handler for legend clicks
export const createLegendHighlightHandler = () => {
    return (event: any, legendItem: any, legend: any) => {
        const chart = legend.chart;
        const datasetIndex = legendItem.datasetIndex;
        const index = legendItem.index;
        const dataset = chart.data.datasets[datasetIndex];
        
        // Store original colors if not already stored
        if (!dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BACKGROUND]) {
            dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BACKGROUND] = [...dataset.backgroundColor];
            dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BORDER] = [...dataset.borderColor];
        }
        
        // Check if this item is currently highlighted
        const isCurrentlyHighlighted = dataset[CHART_HIGHLIGHT.STORAGE_KEYS.HIGHLIGHTED_INDEX] === index;
        
        if (isCurrentlyHighlighted) {
            // Remove highlight - restore original colors
            restoreOriginalColors(dataset);
        } else {
            // Apply highlight - dim all segments except the selected one
            applyHighlight(dataset, index);
        }
        
        // Update the chart with smooth transition
        chart.update(CHART_HIGHLIGHT.UPDATE_MODE);
    };
};

// Helper function to restore original colors
const restoreOriginalColors = (dataset: any) => {
    dataset.backgroundColor = [...dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BACKGROUND]];
    dataset.borderColor = [...dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BORDER]];
    dataset[CHART_HIGHLIGHT.STORAGE_KEYS.HIGHLIGHTED_INDEX] = null;
};

// Helper function to apply highlight effect
const applyHighlight = (dataset: any, highlightIndex: number) => {
    const originalBackground = dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BACKGROUND];
    const originalBorder = dataset[CHART_HIGHLIGHT.STORAGE_KEYS.ORIGINAL_BORDER];
    
    // Create dimmed colors for all segments except the highlighted one
    const highlightedBackgroundColors = originalBackground.map((color: string, index: number) => {
        return index === highlightIndex ? color : adjustColorOpacity(color, CHART_HIGHLIGHT.DIMMED_OPACITY);
    });
    
    const highlightedBorderColors = originalBorder.map((color: string, index: number) => {
        return index === highlightIndex ? color : adjustColorOpacity(color, CHART_HIGHLIGHT.DIMMED_OPACITY);
    });
    
    // Apply the highlighting
    dataset.backgroundColor = highlightedBackgroundColors;
    dataset.borderColor = highlightedBorderColors;
    dataset[CHART_HIGHLIGHT.STORAGE_KEYS.HIGHLIGHTED_INDEX] = highlightIndex;
};
